import { Alert, Button } from 'ui';
import { AlertIcon } from 'ui/icons';
import type {
  CommunicationAttachmentPreviewClassRow,
  CommunicationAttachmentPreviewItem,
  CommunicationAttachmentPreviewRecipient,
  CommunicationAttachmentPreviewResponse,
} from 'api';

interface IssuerCommunicationConfirmationProps {
  preview: CommunicationAttachmentPreviewResponse;
  isSubmitting: boolean;
  onBack: () => void;
  onConfirm: () => void;
}

const renderAttachmentItems = (items?: CommunicationAttachmentPreviewItem[]) => {
  const availableItems = items?.filter(item => item.applicable !== false);

  if (!availableItems?.length) {
    return <span className="text-gray-400">None</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {availableItems.map((item, index) => (
        <span
          key={`${item.type}-${item.label}-${index}`}
          className="rounded-sm border border-blue-100 bg-blue-50 px-2 py-1 text-xs leading-4 text-blue-700"
        >
          {item.label}
        </span>
      ))}
    </div>
  );
};

const getRecipientSubtitle = (recipient: CommunicationAttachmentPreviewRecipient) => {
  if (recipient.holder_external_id) return recipient.holder_external_id;

  return `Holder #${recipient.shareholder_id}`;
};

const getRecipientClassRows = (
  recipient: CommunicationAttachmentPreviewRecipient,
): CommunicationAttachmentPreviewClassRow[] => {
  if (recipient.classes?.length) return recipient.classes;

  return [
    {
      class_name: 'No class',
      reports: [],
      certificates: [],
    },
  ];
};

export const IssuerCommunicationConfirmation = ({
  preview,
  isSubmitting,
  onBack,
  onConfirm,
}: IssuerCommunicationConfirmationProps) => {
  const outgoingEmailsCount = preview.recipients.reduce(
    (total, recipient) => total + (recipient.outgoing_email_count ?? 0),
    0,
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-medium text-gray-900">Review & Send Emails</h2>
        <p className="mt-2 text-sm text-gray-600">
          Confirm recipients and generated attachments before sending.
        </p>
      </div>

      <div className="max-h-[460px] overflow-auto border border-gray-200">
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 z-[1] bg-gray-100 text-gray-500">
            <tr className="h-9 border-b">
              <th className="w-[28%] border-r border-gray-200 px-3 font-medium">Recipient</th>
              <th className="w-[22%] px-3 font-medium">Class</th>
              <th className="w-[25%] px-3 font-medium">Reports</th>
              <th className="w-[25%] px-3 font-medium">Certificates</th>
            </tr>
          </thead>
          <tbody>
            {preview.recipients.map(recipient => {
              const classRows = getRecipientClassRows(recipient);
              const additionalEmails =
                recipient.additional_emails?.filter(email => email.trim()) ?? [];

              return classRows.map((classRow, classIndex) => (
                <tr
                  key={`${recipient.shareholder_id}-${classRow.class_id ?? classIndex}`}
                  className="align-top [&:not(:last-child)]:border-b"
                >
                  {classIndex === 0 && (
                    <td className="border-r border-gray-200 px-3 py-3" rowSpan={classRows.length}>
                      <div className="font-medium text-gray-900">{recipient.holder_name}</div>
                      <div className="text-gray-500">{getRecipientSubtitle(recipient)}</div>
                      {recipient.email && (
                        <div className="mt-2 space-y-0.5">
                          <div className="text-[10px] uppercase leading-4 text-gray-400">
                            Primary email
                          </div>
                          <div className="text-gray-500">{recipient.email}</div>
                        </div>
                      )}
                      {!!additionalEmails.length && (
                        <div className="mt-2 space-y-0.5">
                          <div className="text-[10px] uppercase leading-4 text-gray-400">
                            Additional emails
                          </div>
                          {additionalEmails.map(email => (
                            <div key={email} className="text-gray-500">
                              {email}
                            </div>
                          ))}
                        </div>
                      )}
                      {recipient.skipped && !!recipient.skipped_reasons?.length && (
                        <div className="mt-2 rounded-sm border border-orange-200 bg-orange-50 px-2 py-1 text-orange-700">
                          {recipient.skipped_reasons.join('. ')}
                        </div>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-3 text-gray-900">
                    <div>{classRow.class_name}</div>
                  </td>
                  <td className="px-3 py-3">{renderAttachmentItems(classRow.reports)}</td>
                  <td className="px-3 py-3">{renderAttachmentItems(classRow.certificates)}</td>
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>

      {outgoingEmailsCount === 0 && (
        <Alert icon={AlertIcon} variant="error" className="mb-0">
          <div className="whitespace-normal">
            No emails will be sent because no selected recipients have available generated
            attachments.
          </div>
        </Alert>
      )}

      <div className="flex gap-3">
        <Button variant="secondary" onClick={onBack}>
          Back
        </Button>
        <Button disabled={outgoingEmailsCount === 0} loading={isSubmitting} onClick={onConfirm}>
          Confirm & Send ({outgoingEmailsCount} {outgoingEmailsCount === 1 ? 'email' : 'emails'})
        </Button>
      </div>
    </div>
  );
};
